import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactElement, ReactNode } from "react";
import type { OctantParentPageTemplate } from "../../services/octant/octantPageService";
import FlipCardGrid from "../../components/FlipCardGrid";

interface OctantPageProps {
  pageId?: string;
  template: OctantParentPageTemplate;
  capabilitiesRenderer?: (section: OctantParentPageTemplate["capabilitiesSection"]) => ReactNode;
}

export default function OctantPage({
  pageId,
  template,
  capabilitiesRenderer,
}: OctantPageProps): ReactElement {
  const typedPageData = template;
  const heroVideoSrc = "https://cdn.mydecisive.ai/media/latest/octant-demo.mp4";

  return (
    <Box
      component="main"
      id={pageId}
      sx={{
        px: 0,
        py: 0,
        "@keyframes octantPulse": {
          "0%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.035)" },
          "100%": { transform: "scale(1)" },
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          mx: "auto",
          p: 0,
        }}
      >
        <Box
          sx={{
            overflow: "hidden",
            color: "inherit",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                p: { xs: 3, md: 6 },
              }}
            >
              <Typography
                component="h1"
                sx={{
                  fontFamily: 'Geist, Helvetica, Arial, sans-serif',
                  fontSize: { xs: "2rem", md: "4rem" },
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  maxWidth: { md: "12ch" },
                }}
              >
                {typedPageData.hero.title}
              </Typography>
              <Typography
                sx={{
                  maxWidth: { md: "34ch" },
                  fontSize: { xs: "1rem", md: "1.25rem" },
                }}
              >
                {typedPageData.hero.subtitle}
              </Typography>
              <Button
                href={typedPageData.hero.ctaHref}
                variant="outlined"
                sx={{
                  alignSelf: "flex-start",
                  mt: 2,
                  minHeight: 40,
                  textTransform: "none",
                  borderRadius: "4px",
                  fontWeight: 700,
                  fontFamily: 'Geist, Helvetica, Arial, sans-serif',
                  boxSizing: "border-box",
                  borderWidth: "1px",
                  transition: "border-color 200ms ease, background-color 200ms ease, color 200ms ease",
                  "&&, &&:hover, &&:focus-visible, &&:active": {
                    padding: "8px 20px !important",
                    borderRadius: "4px",
                    borderWidth: "1px",
                    boxShadow: "none",
                  },
                }}
              >
                {typedPageData.hero.ctaText}
              </Button>
            </Stack>

            <Box
              sx={{
                minHeight: { xs: 220, md: 420 },
                position: "relative",
                borderLeft: { md: "1px solid rgba(255, 255, 255, 0.12)" },
              }}
            >
              <Box sx={{ ml: 4 }}>
                <video
                  autoPlay={false}
                  controls
                  muted
                  width={450}
                  height={450}
                >
                  <source src={heroVideoSrc}></source>
                </video>
              </Box>
            </Box>
          </Box>

          {capabilitiesRenderer ? (
            capabilitiesRenderer(typedPageData.capabilitiesSection)
          ) : (
            <FlipCardGrid
              title={typedPageData.capabilitiesSection.title}
              cards={typedPageData.capabilitiesSection.cards}
              columns={2}
            />
          )}
        </Box>

        <Box
          sx={{
            mt: 0,
            py: { xs: 1.5, md: 2.2 },
            px: { xs: 2, md: 3 },
            textAlign: "center",
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Geist, Helvetica, Arial, sans-serif',
              fontSize: { xs: "1.25rem", md: "2rem" },
            }}
          >
            {typedPageData.modulesSection.title}
          </Typography>
          {typedPageData.modulesSection.links.length > 0 && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              sx={{ mt: 2, justifyContent: "center", alignItems: "center" }}
            >
              {typedPageData.modulesSection.links.map((link) => (
                <Button
                  key={link.href}
                  href={link.href}
                  variant="outlined"
                  sx={{
                    minHeight: 40,
                    textTransform: "none",
                    color: "inherit",
                    borderColor: "currentColor",
                    boxSizing: "border-box",
                    transition: "border-color 200ms ease, background-color 200ms ease, color 200ms ease",
                    "&&, &&:hover, &&:focus-visible, &&:active": {
                      padding: "6px 16px !important",
                      borderRadius: "4px",
                      borderWidth: "1px",
                      borderColor: "currentColor",
                      boxShadow: "none",
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
